import {
    AfterViewInit,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { environment } from 'src/environments/environment';
import { Subject } from 'rxjs';
import { DataTableDirective } from 'angular-datatables';
import { helper } from 'src/environments/helper';
import { OperatorSigningLogsService } from './operator-signing-logs.service';
import { OperatorSigningLogItem, StatusOption } from './operator-signing-logs.interface';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-operator-signing-logs',
    templateUrl: './operator-signing-logs.component.html',
    styleUrls: ['./operator-signing-logs.component.scss'],
})
export class OperatorSigningLogsComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    helper = helper;

    isLoading = false;
    showCard = false;
    isExporting = false;

    // Filters
    dateFrom: string = '';
    dateTo: string = '';
    statusFilter: string = '';
    searchFilter: string = '';
    userIdFilter: string = '';
    userNameFilter: string = '';
    documentGeneratIdFilter: string = '';
    documentRegistraturaIdFilter: string = '';
    docExemplarTypeFilter: string = '';

    // Dropdown options
    statuses: StatusOption[] = [];
    exemplarTypes = [
        { value: 'operator', label: 'Operator' },
        { value: 'cetatean', label: 'Cetatean' }
    ];

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();

    constructor(
        private operatorSigningLogsService: OperatorSigningLogsService
    ) {}

    ngOnInit(): void {
        this.loadStatuses();
        this.renderDT();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    loadStatuses(): void {
        this.operatorSigningLogsService.getStatuses().subscribe({
            next: (response) => {
                if (response.statuses) {
                    this.statuses = response.statuses;
                }
            },
            error: (err) => {
                console.error('Error loading statuses:', err);
            }
        });
    }

    renderDT(): void {
        const self = this;
        this.dtOptions = {
            serverSide: true,
            searching: false,
            responsive: true,
            pageLength: 25,
            order: [[0, 'desc']],

            ajax: (dataTablesParameters: any, callback: any) => {
                self.isLoading = true;

                const params: any = {
                    draw: dataTablesParameters.draw,
                    start: dataTablesParameters.start,
                    length: dataTablesParameters.length,
                };

                // Sorting
                if (dataTablesParameters.order && dataTablesParameters.order.length > 0) {
                    const orderCol = dataTablesParameters.order[0].column;
                    const orderDir = dataTablesParameters.order[0].dir;
                    const columns = ['id', 'user_name', 'directie_name', 'document_generat_id', 'doc_exemplar_type', 'status', 'has_user_signature', 'created_at'];
                    params.sort_by = columns[orderCol] || 'id';
                    params.sort_dir = orderDir;
                }

                // Filters
                if (self.dateFrom) params.date_from = self.dateFrom;
                if (self.dateTo) params.date_to = self.dateTo;
                if (self.statusFilter) params.status = self.statusFilter;
                if (self.searchFilter) params.search = self.searchFilter;
                if (self.userIdFilter) params.user_id = self.userIdFilter;
                if (self.userNameFilter) params.user_name = self.userNameFilter;
                if (self.documentGeneratIdFilter) params.document_generat_id = self.documentGeneratIdFilter;
                if (self.documentRegistraturaIdFilter) params.document_registratura_id = self.documentRegistraturaIdFilter;
                if (self.docExemplarTypeFilter) params.doc_exemplar_type = self.docExemplarTypeFilter;

                self.operatorSigningLogsService.getOperatorSigningLogs(params).subscribe({
                    next: (resp: any) => {
                        self.isLoading = false;
                        callback({
                            recordsTotal: resp.recordsTotal || 0,
                            recordsFiltered: resp.recordsFiltered || 0,
                            data: resp.data || [],
                        });
                    },
                    error: (err) => {
                        self.isLoading = false;
                        console.error('Error fetching operator signing logs:', err);
                        callback({
                            recordsTotal: 0,
                            recordsFiltered: 0,
                            data: [],
                        });
                    }
                });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID',
                    data: 'id',
                    width: '50px',
                },
                {
                    title: 'Utilizator',
                    data: 'user_name',
                    width: '120px',
                    render: (data: string) => data || '-',
                },
                {
                    title: 'Directie / Compartiment',
                    data: 'directie_name',
                    width: '180px',
                    render: (data: string, type: string, row: any) => {
                        const directie = row.directie_name || '-';
                        const compartiment = row.compartiment_name || '';
                        if (compartiment) {
                            return `<div style="word-wrap:break-word;white-space:normal;"><strong>${directie}</strong><br><small class="text-muted">${compartiment}</small></div>`;
                        }
                        return `<div style="word-wrap:break-word;white-space:normal;"><strong>${directie}</strong></div>`;
                    },
                },
                {
                    title: 'Document',
                    data: 'document_generat_id',
                    render: (data: number | null, type: string, row: any) => {
                        const genId = row.document_generat_id;
                        const genName = row.document_generat_name || '';
                        const regId = row.document_registratura_id;
                        const regType = row.document_registratura_type || '';
                        return `<div style="font-size:11px;word-wrap:break-word;white-space:normal;">
                            ${genId ? `<div><span class="badge bg-info">G:(${genId})</span> ${genName}</div>` : ''}
                            ${regId ? `<div style="margin-top:3px;"><span class="badge bg-secondary">R:(${regId})</span> ${regType}</div>` : ''}
                        </div>`;
                    },
                },
                {
                    title: 'Tip',
                    data: 'doc_exemplar_type',
                    width: '70px',
                    render: (data: string) => {
                        if (!data) return '-';
                        const colors: Record<string, string> = {
                            'operator': 'primary',
                            'cetatean': 'info',
                        };
                        const color = colors[data] || 'secondary';
                        const label = data === 'operator' ? 'OP' : 'CET';
                        return `<span class="badge bg-${color}" title="${data}">${label}</span>`;
                    },
                },
                {
                    title: 'Status',
                    data: 'status',
                    width: '70px',
                    render: (data: string) => {
                        const statusColors: Record<string, string> = {
                            'initiated': 'warning',
                            'success': 'success',
                            'failed': 'danger',
                        };
                        const color = statusColors[data?.toLowerCase()] || 'secondary';
                        return `<span class="badge bg-${color}">${data || '-'}</span>`;
                    },
                },
                {
                    title: 'Semn.',
                    data: 'has_user_signature',
                    width: '50px',
                    render: (data: boolean) => {
                        return data
                            ? '<span class="badge bg-success">Da</span>'
                            : '<span class="badge bg-secondary">Nu</span>';
                    },
                },
                {
                    title: 'Data',
                    data: 'created_at',
                    width: '130px',
                    render: (data: string) => {
                        if (!data) return '-';
                        try {
                            const date = new Date(data);
                            return date.toLocaleString('ro-RO', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            });
                        } catch {
                            return data;
                        }
                    },
                },
                {
                    title: 'Actiuni',
                    data: null,
                    orderable: false,
                    className: 'all',
                    render: (row: any) => {
                        const docLink = row.document_registratura_id
                            ? `<a class="action-btn open-doc ms-1" href="#/documente?id=${row.document_registratura_id}" target="_blank" title="Deschide document">
                                <i class="fa-solid fa-external-link-alt"></i>
                               </a>`
                            : '';
                        return `
                        <div class="d-flex align-center justify-content-left">
                            <button class="action-btn view ms-1" title="Vizualizeaza detalii" data-id="${row.id}">
                                <i class="fa-solid fa-eye"></i>
                            </button>
                            ${docLink}
                        </div>
                        `;
                    },
                },
            ],
            rowCallback: (row: Node, data: OperatorSigningLogItem) => {
                const lastTd = (row as HTMLElement).querySelector('td:last-child');
                const viewBtn = lastTd?.querySelector('.view') as HTMLElement;

                if (viewBtn) {
                    viewBtn.addEventListener('click', () => {
                        this.viewDetails(data);
                    });
                }

                return row;
            },
        };
    }

    rerenderDT(paginate: boolean = false): void {
        if (this.dtElement && this.dtElement.dtInstance) {
            this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
                dtInstance.draw(paginate);
            });
        }
        this.showCard = false;
    }

    toggleCard(): void {
        this.showCard = !this.showCard;
    }

    resetForm(): void {
        this.dateFrom = '';
        this.dateTo = '';
        this.statusFilter = '';
        this.searchFilter = '';
        this.userIdFilter = '';
        this.userNameFilter = '';
        this.documentGeneratIdFilter = '';
        this.documentRegistraturaIdFilter = '';
        this.docExemplarTypeFilter = '';
        this.rerenderDT();
        this.showCard = true;
    }

    applyFilters(): void {
        this.rerenderDT(true);
    }

    viewDetails(data: OperatorSigningLogItem): void {
        this.operatorSigningLogsService.getOperatorSigningLogDetail(data.id).subscribe({
            next: (response: any) => {
                const detail = response.data || response;
                const statusColors: Record<string, string> = {
                    'success': '#28a745',
                    'initiated': '#ffc107',
                    'failed': '#dc3545',
                };
                const statusColor = statusColors[detail.status?.toLowerCase()] || '#6c757d';

                const formatDate = (dateStr: string) => {
                    if (!dateStr) return '-';
                    try {
                        return new Date(dateStr).toLocaleString('ro-RO', {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit', second: '2-digit'
                        });
                    } catch { return dateStr; }
                };

                const htmlContent = `
                    <div style="text-align: left; font-size: 14px;">
                        <table style="width: 100%; border-collapse: collapse;">
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600; width: 160px;">ID:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.id || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Status:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">
                                    <span style="background: ${statusColor}; color: white; padding: 3px 10px; border-radius: 4px; font-size: 12px;">
                                        ${detail.status || '-'}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Utilizator:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.user_name || '-'} ${detail.user_id ? '(ID: ' + detail.user_id + ')' : ''}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Directie:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.directie_name || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Compartiment:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.compartiment_name || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Doc Generat ID:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.document_generat_id || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Doc Registratura ID:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.document_registratura_id || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Tip Exemplar:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.doc_exemplar_type || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Cu Semnatura User:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.has_user_signature ? 'Da' : 'Nu'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">IP:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.ip_address || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Creat la:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${formatDate(detail.created_at)}</td>
                            </tr>
                            ${detail.error_message ? `
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600; color: #dc3545;">Eroare:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; color: #dc3545;">${detail.error_message}</td>
                            </tr>
                            ` : ''}
                            ${detail.error_code ? `
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600; color: #dc3545;">Cod Eroare:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; color: #dc3545;">${detail.error_code}</td>
                            </tr>
                            ` : ''}
                        </table>
                    </div>
                `;

                const swalOptions: any = {
                    title: '<i class="fa-solid fa-file-signature"></i> Detalii Log Semnare Operator',
                    html: htmlContent,
                    width: 650,
                    confirmButtonText: 'Inchide',
                    confirmButtonColor: '#3085d6',
                };

                // Add "Vizualizeaza Document" button if document_registratura_id exists
                if (detail.document_registratura_id) {
                    swalOptions.showDenyButton = true;
                    swalOptions.denyButtonText = '<i class="fa-solid fa-external-link-alt"></i> Vizualizeaza Document';
                    swalOptions.denyButtonColor = '#17a2b8';
                }

                Swal.fire(swalOptions).then((result: any) => {
                    if (result.isDenied && detail.document_registratura_id) {
                        window.open(`#/documente?id=${detail.document_registratura_id}`, '_blank');
                    }
                });
            },
            error: (err: any) => {
                console.error('Error fetching log detail:', err);
                Swal.fire({
                    title: 'Eroare',
                    text: 'Nu s-au putut incarca detaliile.',
                    icon: 'error',
                    confirmButtonText: 'Inchide',
                });
            }
        });
    }

    exportCsv(): void {
        this.isExporting = true;
        const params: any = {};
        if (this.dateFrom) params.date_from = this.dateFrom;
        if (this.dateTo) params.date_to = this.dateTo;
        if (this.statusFilter) params.status = this.statusFilter;
        if (this.searchFilter) params.search = this.searchFilter;
        if (this.userIdFilter) params.user_id = this.userIdFilter;
        if (this.userNameFilter) params.user_name = this.userNameFilter;
        if (this.documentGeneratIdFilter) params.document_generat_id = this.documentGeneratIdFilter;
        if (this.documentRegistraturaIdFilter) params.document_registratura_id = this.documentRegistraturaIdFilter;
        if (this.docExemplarTypeFilter) params.doc_exemplar_type = this.docExemplarTypeFilter;

        this.operatorSigningLogsService.exportCsv(params).subscribe({
            next: (blob) => {
                this.isExporting = false;
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `operator-signing-logs-${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            },
            error: (err) => {
                this.isExporting = false;
                console.error('Error exporting CSV:', err);
            }
        });
    }
}
